import './styles.scss';

import React, {useEffect, useState} from 'react';
import {
  MenuItem,
  Select,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import moment from 'moment';
import {Notify, Confirm, Loading} from 'notiflix';
import {Axios} from 'services/axios';

// Custom components for typography, box, badge, button, and table
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import MDButton from 'components/MDButton';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Feature Name', accessor: 'name', width: '20%', align: 'left'},
  {
    Header: 'Description',
    accessor: 'description',
    width: '20%',
    align: 'center',
  },
  {
    Header: 'Dflt. Price',
    accessor: 'featureDefaultPrice',
    width: '20%',
    align: 'center',
  },
  {Header: 'Created at', accessor: 'createdAt', align: 'center'},
  {Header: 'Status', accessor: 'status', align: 'center'},
  {Header: 'SubCategory', accessor: 'subCategoryName', align: 'center'},
  {Header: 'Action', accessor: 'action', align: 'center'},
];

const getRow = (feature, idx, handleEdit, handleDelete) => ({
  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  name: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {feature.name}
    </MDTypography>
  ),
  description: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {feature.description}
    </MDTypography>
  ),
  featureDefaultPrice: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {feature.featureDefaultPrice}
    </MDTypography>
  ),
  createdAt: (
    <MDTypography variant="caption">
      {moment(feature.createdAt).calendar()}
    </MDTypography>
  ),
  status: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={feature.active ? 'Active' : 'Inactive'}
        color={feature.active ? 'success' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  subCategoryName: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {feature.subCategoryName}
    </MDTypography>
  ),
  action: (
    <MDBox ml={-1} display="flex" gap={1}>
      <MDBadge
        badgeContent="edit"
        color="info"
        variant="gradient"
        size="sm"
        onClick={() => handleEdit(feature.id)}
        style={{cursor: 'pointer'}}
      />
      <MDBadge
        badgeContent="Delete"
        color="error"
        variant="gradient"
        size="sm"
        onClick={() => handleDelete(feature.id)}
        style={{cursor: 'pointer'}}
      />
    </MDBox>
  ),
});

const AdditionalFeaturesTable = ({
  allAdditionalFeaturesWithCategoryAndSubCategory = [],
}) => {
  const [features, setFeatures] = useState([]);
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [uniqueSubCategories, setUniqueSubCategories] = useState([]);

  // State for editing an additional feature
  const [openEditFeatureDialog, setOpenEditFeatureDialog] = useState(false);
  const [editFeatureForm, setEditFeatureForm] = useState({
    id: '',
    subCategoryId: '',
    subCategoryName: '',
    name: '',
    description: '',
    featureDefaultPrice: '',
    active: false,
  });

  // Flatten data from subcategories into a single features array
  useEffect(() => {
    const flattened = [];
    const uniques = [];
    allAdditionalFeaturesWithCategoryAndSubCategory.forEach(sub => {
      uniques.push({
        id: sub.id,
        subCategoryName: sub.subCategoryName,
      });
      if (sub.features && Array.isArray(sub.features)) {
        sub.features.forEach(feature => {
          flattened.push({
            ...feature,
            subCategoryId: sub.id,
            subCategoryName: sub.subCategoryName,
          });
        });
      }
    });
    setFeatures(flattened);
    setUniqueSubCategories(uniques);
  }, [allAdditionalFeaturesWithCategoryAndSubCategory]);

  // Filter features based on the selected subcategory.
  const filteredFeatures =
    filterSubCategory === 'all'
      ? features
      : features.filter(f => f.subCategoryId === filterSubCategory);

  // Open edit dialog and populate form when editing a feature.
  const handleEditFeature = id => {
    const feat = features.find(f => f.id === id);
    if (feat) {
      setEditFeatureForm({
        id: feat.id,
        subCategoryId: feat.subCategoryId,
        subCategoryName: feat.subCategoryName,
        name: feat.name,
        description: feat.description,
        featureDefaultPrice: feat.featureDefaultPrice,
        active: feat.active,
      });
      setOpenEditFeatureDialog(true);
    }
  };

  // Delete feature.
  const handleDeleteFeature = id => {
    Confirm.show(
      'Delete Feature?',
      'Do you agree to delete this feature?',
      'Yes',
      'No',
      async () => {
        try {
          Loading.circle('Deleting feature');
          const feat = features.find(f => f.id === id);
          await Axios.delete(
            `/shop/deleteAdditionalFeature/${id}?label=${encodeURIComponent(feat?.name || '')}`,
          );
          Notify.success('Feature deleted successfully');
          setFeatures(prev => prev.filter(f => f.id !== id));
        } catch (err) {
          console.error(err);
          Notify.failure('Failed to delete feature');
        } finally {
          Loading.remove('Deleting feature');
        }
      },
      () => {},
      {
        width: '320px',
        borderRadius: '8px',
        backgroundColor: '#fff',
      },
    );
  };

  // Handle changes in the edit form.
  const handleEditChange = e => {
    const {name, value, type, checked} = e.target;
    setEditFeatureForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Submit updated feature.
  const handleUpdateFeature = async e => {
    e.preventDefault();

    const payload = {
      features: [
        {
          id: editFeatureForm.id,
          name: editFeatureForm.name,
          description: editFeatureForm.description,
          featureDefaultPrice: editFeatureForm.featureDefaultPrice,
          active: editFeatureForm.active,
        },
      ],
    };

    try {
      Loading.circle('Updating additional feature');
      const response = await Axios.post(
        `/shop/addAdditionalFeaturesToSubCategory/${editFeatureForm.subCategoryId}`,
        payload,
      );
      Notify.success(response.data.message || 'Feature updated successfully');
      // Update the features state with the updated feature.
      setFeatures(prev =>
        prev.map(f =>
          f.id === editFeatureForm.id ? {...f, ...editFeatureForm} : f,
        ),
      );
      setOpenEditFeatureDialog(false);
    } catch (err) {
      console.error(err);
      Notify.failure(err.response?.data?.message || 'Failed to update feature');
    } finally {
      Loading.remove('Updating additional feature');
    }
  };

  return (
    <>
      <MDBox mb={2} px={2}>
        {/* <MDTypography mb={2} variant="body1">
          All Additional Features
        </MDTypography> */}
        <InputLabel id="filterByCategory-label">Select SubCategory</InputLabel>
        <Select
          labelId="filterByCategory-label"
          id="filterByCategory"
          label="Filter by SubCategory"
          value={filterSubCategory}
          onChange={e => setFilterSubCategory(e.target.value)}
          variant="outlined"
          sx={{width: 300, height: 40, marginTop: 1.5}}>
          <MenuItem value="all">All Subcategories</MenuItem>
          {uniqueSubCategories.map(sub => (
            <MenuItem key={sub.id} value={sub.id}>
              {sub.subCategoryName}
            </MenuItem>
          ))}
        </Select>
      </MDBox>

      {/* DataTable listing all additional features */}
      <MDBox mt={3}>
        <DataTable
          table={{
            columns,
            rows: (filteredFeatures || []).map((feature, idx) =>
              getRow(feature, idx, handleEditFeature, handleDeleteFeature),
            ),
          }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
          hoverEffect
        />
      </MDBox>

      {/* Edit Additional Feature Modal */}
      <Dialog
        open={openEditFeatureDialog}
        onClose={() => setOpenEditFeatureDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Edit Additional Feature</DialogTitle>
        <DialogContent>
          <form onSubmit={handleUpdateFeature}>
            {/* Render the SubCategory name as read-only */}
            <MDTypography variant="body2" sx={{mb: 2}}>
              SubCategory:{' '}
              <MDTypography variant="button" fontWeight="medium">
                {editFeatureForm.subCategoryName}
              </MDTypography>
            </MDTypography>

            <TextField
              label="Feature Name"
              fullWidth
              margin="normal"
              name="name"
              value={editFeatureForm.name}
              onChange={handleEditChange}
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              name="description"
              value={editFeatureForm.description}
              onChange={handleEditChange}
            />
            <TextField
              label="Feature Default Price"
              fullWidth
              margin="normal"
              type="number"
              name="featureDefaultPrice"
              value={editFeatureForm.featureDefaultPrice}
              onChange={handleEditChange}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFeatureForm.active}
                  onChange={handleEditChange}
                  name="active"
                />
              }
              label="Active"
            />
            <DialogActions sx={{mt: 2}}>
              <MDButton
                onClick={() => setOpenEditFeatureDialog(false)}
                color="secondary"
                variant="outlined">
                Cancel
              </MDButton>
              <MDButton type="submit" color="info" variant="contained">
                Update
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdditionalFeaturesTable;
